import { useState, useEffect, useCallback } from 'react';
import authService from '../../../services/authService';
import toastService from '../../../services/toastService';

/**
 * Custom hook for managing available scanners and tool permissions
 */
export const useScanners = (pentestServer, userRole) => {
  const [scanners, setScanners] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [isLoadingScanners, setIsLoadingScanners] = useState(false);
  const [scannersError, setScannersError] = useState(null);

  // Role hierarchy for permission checking
  const roleHierarchy = { 'user': 1, 'superuser': 2, 'admin': 3 };

  // Helper function to check if user has permission for a tool
  const hasToolPermission = useCallback((requiredRole) => {
    const userRoleLevel = roleHierarchy[userRole] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;
    return userRoleLevel >= requiredRoleLevel;
  }, [userRole]);

  // Fetch available scanners from server
  const fetchScanners = useCallback(async (setDefaults = true) => {
    setIsLoadingScanners(true);
    setScannersError(null);

    try {
      const response = await authService.authenticatedCall(`${pentestServer}/scanners`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch scanners: ${response.status}`);
      }

      const scannersData = await response.json();
      setScanners(scannersData);

      // Only set default selected tools if setDefaults is true and no tools are currently selected
      if (setDefaults && selectedTools.length === 0) {
        const defaultTools = scannersData
          .filter(scanner => hasToolPermission(scanner.requiredRole || 'user'))
          .filter(scanner => scanner.category === 'basic' || scanner.isDefault)
          .map(scanner => scanner.id);
        
        setSelectedTools(defaultTools);
      }
    } catch (error) {
      console.error('Error fetching scanners:', error);
      setScannersError(error.message);
      toastService.error('Failed to load available scanners');
    } finally {
      setIsLoadingScanners(false);
    }
  }, [pentestServer, hasToolPermission, selectedTools.length]);

  // Load scanners on mount or when server/role changes (with defaults)
  useEffect(() => {
    if (pentestServer && userRole) {
      fetchScanners(true);
    }
  }, [pentestServer, userRole, fetchScanners]);

  // Handle tool selection
  const handleToolSelection = useCallback((toolId, isSelected) => {
    setSelectedTools(prev => {
      if (isSelected) {
        return [...prev, toolId];
      } else {
        return prev.filter(id => id !== toolId);
      }
    });
  }, []);

  // Handle select all tools (only those user has permission for)
  const handleSelectAll = useCallback(() => {
    const availableTools = scanners
      .filter(scanner => hasToolPermission(scanner.requiredRole || 'user'))
      .map(scanner => scanner.id);
    setSelectedTools(availableTools);
  }, [scanners, hasToolPermission]);

  // Handle clear all selections
  const handleClearAll = useCallback(() => {
    setSelectedTools([]);
  }, []);

  // Manually set selected tools (useful for editing existing configurations)
  const setSelectedToolsManually = useCallback((tools) => {
    setSelectedTools(tools);
  }, []);

  // Preset configurations
  const presets = {
    basic: {
      name: 'Basic Scan',
      description: 'Essential security checks suitable for all users',
      tools: ['dns', 'ssl', 'headers', 'nmap']
    },
    web: {
      name: 'Web Application',
      description: 'Comprehensive web application security testing',
      tools: ['dns', 'ssl', 'headers', 'nmap', 'nikto', 'ffuf', 'cors', 'jsendpoints', 'sqlmap', 'webvulns']
    },
    network: {
      name: 'Network Security',
      description: 'Network-focused security assessment',
      tools: ['dns', 'nmap', 'udpports', 'protocols', 'ssl', 'crackmapexec', 'impacket']
    },
    cloud: {
      name: 'Cloud Security',
      description: 'Cloud and storage security assessment',
      tools: ['cloudsecurity', 'cloudexposure']
    },
    ad: {
      name: 'Active Directory',
      description: 'AD and internal network attacks',
      tools: ['bloodhound', 'enum4linux', 'crackmapexec', 'impacket', 'responder']
    },
    exploitation: {
      name: 'Exploitation',
      description: 'Credential attacks and exploitation',
      tools: ['hydra', 'responder', 'metasploit']
    },
    email: {
      name: 'Email Security',
      description: 'Email security checks (SPF, DKIM, DMARC, relay, spoofing)',
      tools: ['emailsecurity']
    },
    advanced: {
      name: 'Advanced Testing',
      description: 'Full security assessment with all available tools',
      tools: [
        'dns', 'ssl', 'headers', 'nmap', 'nikto', 'ffuf', 'sublist3r',
        'sqlmap', 'cors', 'ssrf', 'webvulns', 'jsendpoints', 'apidiscovery',
        'cloudexposure', 'udpports', 'protocols', 'cloudsecurity',
        'crackmapexec', 'impacket', 'enum4linux', 'hydra', 'bloodhound',
        'responder', 'metasploit', 'emailsecurity', 'edravdetection'
      ]
    }
  };


  // Apply preset configuration
  const applyPreset = useCallback((presetName) => {
    const preset = presets[presetName];
    if (!preset) return;

    // Filter tools based on user permissions and availability
    const availablePresetTools = preset.tools.filter(toolId => {
      const scanner = scanners.find(s => s.id === toolId);
      return scanner && hasToolPermission(scanner.requiredRole || 'user');
    });

    setSelectedTools(availablePresetTools);
    toastService.info(`Applied ${preset.name} preset (${availablePresetTools.length} tools selected)`);
  }, [scanners, hasToolPermission]);

  // Get scanners grouped by category
  const scannersByCategory = scanners.reduce((acc, scanner) => {
    const category = scanner.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(scanner);
    return acc;
  }, {});

  // Get available presets based on user role
  const availablePresets = Object.entries(presets).filter(([presetName, preset]) => {
    // Check if user has permission for at least some tools in the preset
    return preset.tools.some(toolId => {
      const scanner = scanners.find(s => s.id === toolId);
      return scanner && hasToolPermission(scanner.requiredRole || 'user');
    });
  });

  return {
    scanners,
    selectedTools,
    isLoadingScanners,
    scannersError,
    scannersByCategory,
    availablePresets,
    presets,
    hasToolPermission,
    handleToolSelection,
    handleSelectAll,
    handleClearAll,
    applyPreset,
    fetchScanners,
    setSelectedToolsManually
  };
};